import React from 'react';

import { FormattedTextBlock } from './FormattedTextBlock';

import { FontSizeProvider } from './FontSizeContext';

import { AudioPlayer } from '../shared/AudioPlayer';

import { LearnerImageCarousel } from './LearnerImageCarousel';

import { LearnerExpandableImage } from './LearnerExpandableImage';

import { loadEditorBlocksFromApi } from '../../utils/adminEditorBlocksPersistence';

import { normalizeAppHostedUploadUrl } from '../../utils/normalizeAppHostedUploadUrl';

import { parseServerImageUrls } from '../../utils/mediaUploadLimits';

import { readImageDisplayModeFromPersistedBlock } from '../../utils/adminImageBlockEditor';

import type { AdminEditorBlockFields } from '../../utils/adminBlockAudioEditor';



type RichContentSource = {

  textContent?: string;

  textContentFa?: string;

  textFormatting?: string;

  textFormattingFa?: string;

  imageUrl?: string;

  audioUrl?: string;

  audioFileName?: string;

  videoUrl?: string;

  originalAudioUrl?: string;

  voiceStylePresetId?: string | null;

};



function parseTextFormatting(formatting?: string) {

  if (!formatting) return undefined;

  try {

    const parsed = JSON.parse(formatting) as unknown;

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {

      return parsed as Record<string, unknown>;

    }

  } catch {

    /* legacy */

  }

  return undefined;

}



function normalizeMediaUrl(url?: string): string {

  if (!url) return '';

  return normalizeAppHostedUploadUrl(url);

}



const LearnerImageStack: React.FC<{ urls: string[]; alt: string; className?: string }> = ({

  urls,

  alt,

  className = 'max-w-full h-auto object-contain rounded-lg border border-slate-200 dark:border-slate-600',

}) => (

  <div className="space-y-3">

    {urls.map((src, i) => (

      <LearnerExpandableImage

        key={`${src}-${i}`}

        src={src}

        alt={`${alt} ${i + 1}`}

        className={className}

      />

    ))}

  </div>

);



const LearnerBlockAudio: React.FC<{ block: AdminEditorBlockFields }> = ({ block }) => {

  const playbackUrl = normalizeMediaUrl(block.audioUrl);

  if (!playbackUrl) return null;



  return (

    <AudioPlayer

      src={playbackUrl}

      controls

      compact

      forceMobileLayout

      className="w-full max-w-full"

    />

  );

};



const LearnerRichContentBlock: React.FC<{

  block: AdminEditorBlockFields;

  imageAlt?: string;

  contentDirection?: 'ltr' | 'rtl' | 'auto';

  searchHighlightTerm?: string;

}> = ({ block, imageAlt = 'Content', contentDirection, searchHighlightTerm }) => {

  if (block.type === 'text' && (block.textContent?.trim() || block.textContentFa?.trim())) {

    return (

      <FontSizeProvider>

        <FormattedTextBlock

          textContent={block.textContent ?? ''}

          formatting={parseTextFormatting(block.textFormatting)}

          {...(contentDirection ? { contentDirection } : {})}

          {...(searchHighlightTerm ? { searchHighlightTerm } : {})}

        />

      </FontSizeProvider>

    );

  }



  if (block.type === 'image' && block.imageUrl?.trim()) {

    const urls = parseServerImageUrls(block.imageUrl).map((u) => normalizeMediaUrl(u));

    if (urls.length === 0) return null;

    const displayMode = readImageDisplayModeFromPersistedBlock(block);

    if (urls.length > 1 && displayMode === 'carousel') {

      return <LearnerImageCarousel urls={urls} alt={imageAlt} />;

    }

    return <LearnerImageStack urls={urls} alt={imageAlt} />;

  }



  if (block.type === 'audio' && block.audioUrl?.trim()) {

    return (

      <div className="w-full min-w-0">

        <LearnerBlockAudio block={block} />

      </div>

    );

  }



  if (block.type === 'video' && block.videoUrl?.trim()) {

    const videoSrc = normalizeMediaUrl(block.videoUrl);

    if (!videoSrc) return null;

    return (

      <div className="flex justify-center w-full min-w-0">

        <video

          src={videoSrc}

          controls

          className="w-full max-w-full h-auto rounded-lg border border-slate-200 dark:border-slate-600"

        />

      </div>

    );

  }



  return null;

};



export const RichContentBlocksView: React.FC<{

  source: RichContentSource;

  imageAlt?: string;

  contentDirection?: 'ltr' | 'rtl' | 'auto';

  searchHighlightTerm?: string;

  className?: string;

}> = ({ source, imageAlt, contentDirection, searchHighlightTerm, className }) => {

  const blocks = React.useMemo(() => loadEditorBlocksFromApi(source), [source]);

  if (!blocks.length) return null;



  return (

    <div className={className ?? 'space-y-2'}>

      {blocks.map((block, index) => (

        <div key={`${block.type}-${block.displayOrder ?? index}-${index}`} className="min-w-0">

          <LearnerRichContentBlock

            block={block}

            imageAlt={imageAlt}

            {...(contentDirection ? { contentDirection } : {})}

            {...(searchHighlightTerm ? { searchHighlightTerm } : {})}

          />

        </div>

      ))}

    </div>

  );

};

